/**
 * Persistent Storage — Capacitor Preferences API wrapper
 * ======================================================
 * Uses native SharedPreferences (Android) / UserDefaults (iOS) for critical flags.
 * Falls back to localStorage in web/browser environments.
 * 
 * Unlike localStorage which gets wiped on uninstall in Capacitor WebView,
 * Preferences API persists on app updates (only wiped on full uninstall).
 * More importantly, it's immune to WebView storage eviction under low memory.
 */

let PreferencesAPI = null;

// Lazy-load Capacitor Preferences (only available in native builds)
const getPreferences = async () => {
  if (PreferencesAPI !== null) return PreferencesAPI;
  try {
    // Check if we're in a Capacitor native environment first
    const isNative = window.Capacitor?.isNativePlatform?.() || false;
    if (!isNative) {
      PreferencesAPI = false;
      return false;
    }
    const mod = await import('@capacitor/preferences');
    PreferencesAPI = mod.Preferences;
    return PreferencesAPI;
  } catch (_) {
    PreferencesAPI = false; // Mark as unavailable
    return false;
  }
};

// Critical keys that MUST survive localStorage eviction/clear
const CRITICAL_KEYS = [
  'past_story_viewed',
  'free_story_used',
  'taraanaya_device_id',
  'device_id',
  'user_id',
  'isAuthenticated',
  'onboarding_completed',
  'is_premium',
  'isPremium',
  'plan_unlocks',
  'plan_id',
  'sage_credits',
  'premium_expires'
];

/**
 * Set a critical flag in BOTH localStorage and Capacitor Preferences.
 * Non-blocking: localStorage is set synchronously, Preferences async.
 */
export const setPersistent = (key, value) => {
  localStorage.setItem(key, value);
  // Async write to native storage (non-blocking)
  getPreferences().then(prefs => {
    if (prefs) prefs.set({ key: `tara_${key}`, value }).catch(() => {});
  });
};

/**
 * Get a critical flag — tries localStorage first, then Capacitor Preferences.
 * Synchronous return from localStorage; async fallback if missing.
 */
export const getPersistent = (key) => {
  return localStorage.getItem(key);
};

/**
 * Async get — checks both localStorage AND Capacitor Preferences.
 * Used on app startup to restore flags that localStorage might have lost.
 */
export const getPersistentAsync = async (key) => {
  // Try localStorage first (fast)
  const localValue = localStorage.getItem(key);
  if (localValue !== null) return localValue;
  
  // Fallback to Capacitor Preferences (survives WebView eviction)
  try {
    const prefs = await getPreferences();
    if (prefs) {
      const result = await prefs.get({ key: `tara_${key}` });
      if (result.value !== null) {
        // Restore to localStorage for fast access next time
        localStorage.setItem(key, result.value);
        return result.value;
      }
    }
  } catch (_) { /* not available */ }
  
  return null;
};

/**
 * Remove from both storages
 */
export const removePersistent = (key) => {
  localStorage.removeItem(key);
  getPreferences().then(prefs => {
    if (prefs) prefs.remove({ key: `tara_${key}` }).catch(() => {});
  });
};

/**
 * Restore all critical flags from Capacitor Preferences → localStorage.
 * Called once on app startup to recover from WebView eviction.
 */
export const restoreCriticalFlags = async () => {
  const prefs = await getPreferences();
  if (!prefs) return; // Not in Capacitor environment
  
  let restored = 0;
  for (const key of CRITICAL_KEYS) {
    const localValue = localStorage.getItem(key);
    if (localValue === null) {
      try {
        const result = await prefs.get({ key: `tara_${key}` });
        if (result.value !== null) {
          localStorage.setItem(key, result.value);
          restored++;
        }
      } catch (_) { /* skip */ }
    }
  }
  if (restored > 0) {
    console.log(`🔐 Restored ${restored} critical flags from native storage`);
  }
};
